import { useEffect, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { MAX_KEY_COUNT } from '@/components/reservations/car-alerts';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';

interface KeyLimitModalProps {
  visible?: boolean;
  onClose: () => void;
  style?: any;
}

const STEPS = [
  'In the car, tap Controls > Locks on the touchscreen.',
  'Find a phone key or key card you no longer use.',
  'Tap the trash icon next to it to remove it.',
];

export function KeyLimitModal({
  visible = false,
  onClose,
  style,
}: KeyLimitModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleClose = () => {
    setShowModal(false);
    onClose?.();
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={handleClose}
    >
      <View style={styles.containerView}>
        <View style={styles.header}>
          <View style={{ flexShrink: 1, gap: 4 }}>
            <Text type="title2">You Are Almost At The Key Limit</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {`A Tesla holds up to ${MAX_KEY_COUNT} keys. Remove a few you don't use so your next key adds cleanly.`}
            </Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={handleClose}
          />
        </View>

        <View style={{ gap: 16 }}>
          {STEPS.map((step, index) => (
            <View key={step} style={styles.step}>
              <View style={styles.stepBadge}>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.inverse }}
                >
                  {index + 1}
                </Text>
              </View>

              <Text
                type="bodyRegular"
                style={{ flex: 1, color: theme.colors.text.secondary }}
              >
                {step}
              </Text>
            </View>
          ))}
        </View>

        <Alert
          type={AlertType.WARNING}
          title="Leave keys marked Eon connected. Those keep your trip working."
        />

        <Button text="Done" type="primary" onPress={handleClose} />
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '95%',
      sm: 500,
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
    padding: 16,
    gap: 24,
  },
  header: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stepBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.colors.progressBar.active.backgroundColor,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
